import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, KuecheAufnahmestand, KuecheQuellmodell } from '../api';
import { IconWarnung, IconLueftung, IconElektro } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, EmptyState, ErrorState, LoadingState, PropertyGrid, StatusBadge,
} from '../muster';

/**
 * Fremdmodelle > Küche > Aufnahme.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE UMKEHR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis zur Festlegung vom 18.08.2026 entstand ein Küchengerät aus einer Zeile
 * der Planauswertung. Das war zu früh: Ein Gerät ist nicht deshalb vorhanden,
 * weil es auf einem Plan steht — es ist vorhanden, weil es im Küchenmodell
 * steht.
 *
 * Diese Seite ist der Anfang der neuen Reihenfolge:
 *
 *     Hostmodell lesen → beide Küchenmodelle inventarisieren
 *          → PDF-Aufnahme zuordnen → Freigabe → Marker schreiben
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE FREIGABE EIN EIGENER SCHRITT IST
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Lauf findet alle Verknüpfungen und schlägt die mit „KUCA" im Namen als
 * Küchenquelle vor. Er gibt sie NICHT frei, auch nicht bei genau zwei
 * Treffern.
 *
 * Der Grund steht in der Zahl darunter: Wird ein Modell übersehen, fehlt sein
 * halber Bestand — und im Ergebnis fehlt nichts, es sieht nur kleiner aus.
 * Diese Art Fehler fällt Monate später auf, wenn jemand ein Gerät sucht, das
 * es gibt.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string; modelle?: Modell[] } }

interface Modell {
  id: string;
  name: string;
  isActive: boolean;
  revitVersion: number;
  rollen?: string[];
}

/** ExportKind.Kuechenaufnahme — beide Küchenmodelle vollständig lesen. */
const KUECHENAUFNAHME = 140;

/**
 * Das Hostmodell trägt die Rolle „fremdmodelle".
 *
 * TP1_TUV_MX_MEP_TGAA_002 ist das Modell, in dem die Küchenmodelle verknüpft
 * sind — und dasselbe, in das später die Marker geschrieben werden. Die Rolle
 * wird hier aufgelöst und nicht an zwei Stellen: Zweimal aufgelöst hieße zwei
 * Wahrheiten darüber, in welches Modell geschrieben wird.
 */
const KOPFTEXT = 'Der Lauf liest das Hostmodell und darin BEIDE verknüpften '
  + 'Küchenmodelle vollständig — jedes Element mit seinen Parametern, seiner '
  + 'Hostkoordinate und seiner Ausdehnung. Einschließlich der Küchenlüftungsdecke und '
  + 'der Leuchten darin, damit diese mit der Elektroplanung verknüpft werden können.';

const HOSTROLLE = 'fremdmodelle';

export function Kuechenaufnahme() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [stand, setStand] = useState<KuecheAufnahmestand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [profilId, setProfilId] = useState<string | null>(null);

  const modell = (projekt?.modelle ?? []).find(
    (m) => m.isActive && (m.rollen ?? []).includes(HOSTROLLE));

  const laden = useCallback(async () => {
    if (!projekt) return;
    try {
      setStand(await api.kuecheAufnahme(projekt.id));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projekt]);

  useEffect(() => { void laden(); }, [laden]);

  useEffect(() => {
    if (!projekt || !modell) return;
    api.profile(projekt.id, modell.revitVersion)
      .then((p) => setProfilId(p.flatMap((x) => x.versions)[0]?.id ?? null))
      .catch(() => setProfilId(null));
  }, [api, projekt, modell?.id, modell?.revitVersion]);

  const aufnehmen = async () => {
    if (!projekt || !modell || !profilId) return;

    setLaeuft(true);
    setMeldung(null);
    try {
      const auftrag = await api.auftragAnlegen({
        projectId: projekt.id,
        exportProfileVersionId: profilId,
        modelIds: [modell.id],
        kinds: [KUECHENAUFNAHME],
      });
      setMeldung(
        `Küchenaufnahme beauftragt: ${auftrag.jobNumber}. Der Lauf liest `
        + `${modell.name} und darin beide verknüpften Küchenmodelle. Das `
        + 'Ergebnis erscheint hier, sobald er durchgelaufen ist — die Seite '
        + 'muss dafür neu geladen werden.');
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const freigeben = async (q: KuecheQuellmodell, wert: boolean) => {
    if (!projekt) return;
    try {
      await api.kuecheQuellmodellFreigabe(projekt.id, q.id, wert);
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  if (fehler) {
    return (
      <>
        <PageHeader titel="Küchenaufnahme" beschreibung={KOPFTEXT} />
        <ErrorState
          meldung="Der Stand der Küchenaufnahme konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => void laden()}
        />
      </>
    );
  }
  if (!stand) {
    return (
      <>
        <PageHeader titel="Küchenaufnahme" beschreibung={KOPFTEXT} />
        <LoadingState text="Stand der Küchenaufnahme wird geladen …" zeilen={6} spalten={3} />
      </>
    );
  }

  const bereit = modell !== undefined && profilId !== null;
  const erwarteteDateien: string[] = stand.konfig?.erwarteteDateienJson
    ? JSON.parse(stand.konfig.erwarteteDateienJson) : [];

  return (
    <div className="seite">
      <PageHeader
        titel="Küchenaufnahme"
        beschreibung={KOPFTEXT}
        meta={`${stand.freigegeben} von ${stand.erwartet} Küchenmodellen freigegeben`}
        aktionen={(
          <button
            type="button"
            className="m-knopf m-knopf-haupt"
            disabled={!bereit || laeuft}
            title={modell
              ? `${modell.name} lesen und darin beide Küchenmodelle `
                + 'vollständig inventarisieren'
              : 'Kein Modell mit der Rolle „fremdmodelle" markiert'}
            onClick={() => void aufnehmen()}
          >
            {laeuft ? 'wird beauftragt …' : 'Küchenmodelle aufnehmen'}
          </button>
        )}
      />

      {meldung && <div className="hinweis" style={{ marginBottom: 14 }}>{meldung}</div>}

      {!modell && (
        <ErrorState
          titel={`Kein aktives Modell trägt die Rolle „${HOSTROLLE}".`}
          meldung={'Ohne sie ist nicht bestimmt, welches Dokument gelesen wird — und '
            + 'geschrieben wird später ausschließlich in dieses. Die Rolle wird unter '
            + '„Einstellungen → Modellzuordnung" gesetzt.'}
        />
      )}

      {/* ── Die Festlegung ───────────────────────────────────────────── */}

      <ContentSection titel="Festlegung">
        {stand.konfig ? (
          <PropertyGrid
            spalten={2}
            eintraege={[
              { beschriftung: 'Hostmodell', wert: stand.konfig.hostModell },
              { beschriftung: 'Erwartete Küchenmodelle',
                wert: stand.konfig.erwarteteQuellmodelle },
              ...(erwarteteDateien.length > 0 ? [{
                beschriftung: 'Erwartete Dateien', breit: true,
                wert: erwarteteDateien.join(', '),
              }] : []),
              {
                beschriftung: 'Marker', breit: true,
                wert: `${stand.konfig.markerFamilie}, Kantenlänge `
                  + `${stand.konfig.markerKantenlaengeM} m, Unterkante Gxx_OKRB + `
                  + `${stand.konfig.markerVersatzUeberEbeneM} m`,
              },
            ]}
          />
        ) : (
          <EmptyState
            titel="Für dieses Projekt ist keine Küchenkonfiguration hinterlegt."
            erklaerung={'Sie legt Hostmodell, Anzahl der Küchenmodelle und Markerhöhe '
              + 'fest — ohne sie wird nichts geraten, und der Lauf bricht ab.'}
          />
        )}
      </ContentSection>

      {/* ── Die gefundenen Verknüpfungen ─────────────────────────────── */}

      <ContentSection
        titel="Küchenmodelle"
        meta={(
          <StatusBadge
            zustand={stand.quellenVollstaendig ? 'gut' : 'warnung'}
            text={`${stand.freigegeben} von ${stand.erwartet} freigegeben`}
          />
        )}
      >

        {stand.quellen.length === 0 ? (
          <EmptyState
            titel="Noch keine Verknüpfung aufgenommen."
            erklaerung={'Der erste Lauf findet die verknüpften Modelle und schlägt die '
              + 'Küchenmodelle vor — freigegeben werden sie hier.'}
            aktion={(
              <button type="button" className="m-knopf m-knopf-haupt"
                disabled={!bereit || laeuft} onClick={() => void aufnehmen()}>
                Küchenmodelle aufnehmen
              </button>
            )}
          />
        ) : (
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Kürzel</th>
                <th>Datei</th>
                <th>Geladen</th>
                <th style={{ textAlign: 'right' }}>Elemente</th>
                <th style={{ textAlign: 'right' }}>Bauteile</th>
                <th>Zuletzt gelesen</th>
                <th>Freigabe</th>
              </tr>
            </thead>
            <tbody>
              {stand.quellen.map((q) => (
                <tr key={q.id}>
                  <td>
                    <strong>{q.kuerzel ?? '—'}</strong>
                    {q.istKuechenquelle && (
                      <span className="marke neutral" style={{ marginLeft: 6 }}>
                        Küche
                      </span>
                    )}
                  </td>
                  <td className="sehr-schwach">{q.dateiname ?? q.linkInstanzName}</td>
                  <td>
                    {q.geladen === false
                      ? <StatusBadge klein zustand="fehler" text="nicht geladen"
                          titel={'Ein ungeladener Link liefert null Elemente. Das '
                            + 'sieht aus wie ein leeres Modell und ist ein ungelesenes.'} />
                      : <StatusBadge klein zustand="gut" text="geladen" />}
                  </td>
                  <td style={{ textAlign: 'right' }}>{q.elementanzahl ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{q.wurzelbauteile ?? '—'}</td>
                  <td className="sehr-schwach">
                    {q.zuletztGelesenAm
                      ? new Date(q.zuletztGelesenAm).toLocaleString('de-DE')
                      : 'nie'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={q.freigegeben ? 'm-knopf' : 'm-knopf m-knopf-haupt'}
                      onClick={() => void freigeben(q, !q.freigegeben)}
                      title={q.freigegeben
                        ? `Freigegeben von ${q.freigegebenVon ?? 'unbekannt'}`
                        : 'Als Küchenquelle freigeben — erst dann wird sie '
                          + 'inventarisiert'}
                    >
                      {q.freigegeben ? 'freigegeben' : 'freigeben'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!stand.quellenVollstaendig && stand.quellen.length > 0 && (
          <div className="hinweis warnung" style={{ marginTop: 12 }}>
            <IconWarnung size={14} /> Solange nicht {stand.erwartet}
            {' '}Küchenmodelle freigegeben sind, ist der Bestand keine
            vollständige Aufnahme — und das sieht man ihm nicht an: Er wäre
            einfach kleiner.
          </div>
        )}
      </ContentSection>

      {/* ── Was der Bestand hergibt ──────────────────────────────────── */}

      <div className="kennzahlen" style={{ marginBottom: 18 }}>
        <Zahl titel="Bauteile aus dem Modell" wert={stand.bestand.bauteile} />
        <Zahl titel="Lüftungsdecken" wert={stand.bestand.lueftungsdecken}
              symbol={<IconLueftung size={14} />} />
        <Zahl titel="Leuchten" wert={stand.bestand.leuchten}
              symbol={<IconElektro size={14} />} />
        <Zahl titel="noch ungeprüft" wert={stand.bestand.ungeprueft}
              warnung={stand.bestand.ungeprueft > 0} />
        <Zahl titel="ohne Raum" wert={stand.bestand.ohneRaum}
              warnung={stand.bestand.ohneRaum > 0} />
        <Zahl titel="ohne Ebene" wert={stand.bestand.ohneEbene}
              warnung={stand.bestand.ohneEbene > 0} />
      </div>

      {stand.bestand.altbestand > 0 && (
        <div className="hinweis" style={{ marginBottom: 18 }}>
          <strong>{stand.bestand.altbestand} Altkomponenten</strong> aus der
          Planauswertung vor der ersten Modellinventarisierung. Sie sind
          vollständig erhalten — mit Historie und jeder Korrektur —, zählen
          aber nicht als Bestand. Bei eindeutiger Zuordnung zu einem realen
          Modellbauteil werden sie übernommen, nicht neu erfunden.
        </div>
      )}

      {/* ── Die PDF-Seite ────────────────────────────────────────────── */}

      <ContentSection
        titel="PDF-Aufnahme"
        beschreibung={'Eine PDF-Beobachtung ist kein Gerät. Sie wird einem realen '
          + 'Modellbauteil zugeordnet — oder ausdrücklich als „ohne Ziel" freigegeben. '
          + 'Offen bleibt keine.'}
      >
        <PropertyGrid
          spalten={3}
          eintraege={[
            { beschriftung: 'Pläne', wert: stand.pdf.plaene },
            { beschriftung: 'Beobachtungen', wert: stand.pdf.beobachtungen },
            { beschriftung: 'davon offen', wert: stand.pdf.offen,
              hinweis: stand.pdf.offen > 0 ? 'Offene Beobachtungen blockieren die Freigabe.' : undefined },
          ]}
        />
      </ContentSection>

      {stand.letzterLauf && (
        <p className="sehr-schwach" style={{ marginTop: 14 }}>
          Letzter Lauf: {stand.letzterLauf.laufart} ·{' '}
          {stand.letzterLauf.zustand} ·{' '}
          {new Date(stand.letzterLauf.gestartet).toLocaleString('de-DE')}
        </p>
      )}
    </div>
  );
}

function Zahl({ titel, wert, warnung, symbol }: {
  titel: string; wert: number; warnung?: boolean; symbol?: React.ReactNode;
}) {
  return (
    <div className="kennzahl">
      <div className="kennzahl-wert"
           style={warnung && wert > 0 ? { color: 'var(--warnung)' } : undefined}>
        {wert}
      </div>
      <div className="kennzahl-name">{symbol} {titel}</div>
    </div>
  );
}
